import { useEffect, useState } from 'react'
import type { Image } from '@publiqa/shared'
import { Picture } from '../ui'

/**
 * Fond du héro en diaporama : les images se succèdent en fondu, toutes les
 * `interval` secondes. Pause au survol et au focus clavier ; aucune animation
 * si l'utilisateur demande moins de mouvement. Flèches et points en bas,
 * comme les vitrines de référence.
 */
export function HeroSlides({
  images,
  interval,
  label,
}: {
  images: Image[]
  interval: number
  label: string
}) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const count = images.length
  const go = (d: number) => setIndex((i) => (i + d + count) % count)

  useEffect(() => {
    if (paused || count < 2) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const t = window.setInterval(() => setIndex((i) => (i + 1) % count), interval * 1000)
    return () => window.clearInterval(t)
  }, [paused, count, interval])

  return (
    <div
      className="hero-slides"
      role="region"
      aria-roledescription="diaporama"
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {images.map((img, i) => (
        <div
          key={`${img.url}-${i}`}
          className={`hero-slide${i === index ? ' on' : ''}`}
          aria-hidden={i !== index}
        >
          {/* La première image est chargée tout de suite, les autres à la demande. */}
          <Picture image={img} className="b-hero-bg" eager={i === 0} />
        </div>
      ))}
      {count > 1 && (
        <div className="hero-controls">
          <button type="button" onClick={() => go(-1)} aria-label="Image précédente">
            ←
          </button>
          <div className="hero-dots">
            {images.map((_, i) => (
              <button
                type="button"
                key={i}
                className={i === index ? 'on' : ''}
                onClick={() => setIndex(i)}
                aria-label={`Image ${i + 1} sur ${count}`}
                aria-current={i === index}
              />
            ))}
          </div>
          <button type="button" onClick={() => go(1)} aria-label="Image suivante">
            →
          </button>
        </div>
      )}
    </div>
  )
}
